"use client";

import React from "react";
import { CircleCheck as CheckCircle2, FileCheck, ShieldCheck, ArrowRight, Clock, Sparkles, ClipboardList, ChevronRight } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function LifecycleHero() {
  const serviceHighlights = [{
    title: "IQ / OQ / PQ Protocols",
    description: "Comprehensive qualification packages aligned with industry regulatory guidelines."
  }, {
    title: "Scheduled Re-Qualification",
    description: "Routine calibration and preventative maintenance to prevent unexpected instrument downtime."
  }, {
    title: "Audit-Ready Documentation",
    description: "Complete traceability reports and certified engineer sign-offs ready for regulatory inspection."
  }];
  return <section id="qualificationlifecyclehero" data-nav="light" className="relative overflow-hidden bg-background py-16 lg:py-24 border-b border-border">
      {/* Subtle background decorative shapes */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 right-1/4 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-24 left-1/3 h-80 w-80 rounded-full bg-accent/40 blur-2xl" />
      </div>

      <div className="page-container mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Main Copy Column */}
          <div className="lg:col-span-7 flex flex-col gap-6 text-left">
            {/* Breadcrumb navigation */}
            <nav aria-label="Breadcrumb">
              <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <li className="flex items-center gap-1.5">
                  <Link to="/HomePage" className="transition-colors hover:text-foreground">
                    Home
                  </Link>
                  <ChevronRight className="size-3 text-muted-foreground/60 shrink-0" />
                </li>
                <li className="flex items-center gap-1.5">
                  <Link to="/ServicesPage" className="transition-colors hover:text-foreground">
                    Services
                  </Link>
                  <ChevronRight className="size-3 text-muted-foreground/60 shrink-0" />
                </li>
                <li className="flex items-center">
                  <span className="font-medium text-foreground" aria-current="page">
                    Qualification &amp; Lifecycle Services
                  </span>
                </li>
              </ol>
            </nav>

            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold gap-1.5 border border-border">
                <ShieldCheck className="size-3.5 text-primary" />
                Regulatory Documentation Support
              </Badge>
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Sparkles className="size-3 text-primary" />
                Ireland &amp; UK Support
              </span>
            </div>

            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
                Analytical Instrument Qualification &amp; Full Lifecycle Management
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                Keep your regulated laboratory fully compliant, accurate, and audit-ready. Antech Solutions delivers application-led qualification protocols, calibration services, and ongoing maintenance for critical analytical equipment.
              </p>
            </div>

            {/* Value Checkpoints */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              <div className="flex items-center gap-2 text-sm text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Zero Compliance Gaps</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Rapid On-Site Engineers</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>OEM-Trained Specialists</span>
              </div>
            </div>

            {/* CTA Group */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span>Schedule Service / Qualification</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/ServicesPage">
                  View All Lab Services
                </Link>
              </Button>
            </div>

            {/* Footnote reassurance */}
            <div className="flex items-center gap-2 pt-2 text-xs text-muted-foreground">
              <Clock className="size-3.5 text-primary shrink-0" />
              <span>Local field engineering support available across Ireland and the UK</span>
            </div>
          </div>

          {/* Graphic / Visual Card Column */}
          <div className="lg:col-span-5">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="relative">
              <Card className="shadow-md border border-border bg-card relative overflow-hidden">
                {/* Header preview of qualification passport */}
                <div className="bg-secondary/70 p-5 border-b border-border flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="size-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                      <FileCheck className="size-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">Lifecycle Service Matrix</h3>
                      <p className="text-xs text-muted-foreground">Regulated Equipment Assurance</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-background text-xs font-mono">
                    VERIFIED
                  </Badge>
                </div>

                <CardContent className="p-5 sm:p-6 space-y-4">
                  {/* Service highlight cards */}
                  {serviceHighlights.map((item, i) => <div key={item.title} data-index={i} className="rounded-lg border border-border/80 bg-background/50 p-3.5 transition-[border-color,background-color] hover:border-primary/40 hover:bg-secondary/40">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 rounded-full p-1 bg-primary/10 text-primary shrink-0">
                          <CheckCircle2 className="size-3.5" />
                        </div>
                        <div className="space-y-1">
                          <h4 className="text-sm font-semibold text-foreground leading-none">
                            {item.title}
                          </h4>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </div>)}

                  {/* Summary Metric Strip */}
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div className="rounded-lg bg-secondary/50 p-3 border border-border text-center flex flex-col justify-center">
                      <span className="block text-sm sm:text-base font-bold text-foreground">Audit-Ready</span>
                      <span className="text-[11px] text-muted-foreground">Documentation</span>
                    </div>
                    <div className="rounded-lg bg-secondary/50 p-3 border border-border text-center">
                      <span className="block text-xl font-bold text-primary">GxP / FDA</span>
                      <span className="text-[11px] text-muted-foreground">Compliance Standards</span>
                    </div>
                  </div>

                  <div className="pt-2">
                    <Button asChild variant="outline" size="sm" className="w-full text-xs">
                      <Link to="/ContactPage" className="inline-flex items-center justify-center gap-1.5">
                        <ClipboardList className="size-3.5" />
                        Request Custom Qualification Plan
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Decorative side accent tag */}
              <div className="hidden sm:flex items-center gap-2 absolute -bottom-4 -right-4 bg-foreground text-background px-4 py-2 rounded-lg shadow-lg text-xs font-medium border border-border/20">
                <ShieldCheck className="size-4 text-primary shrink-0" />
                <span>Certified Engineer Onboarding</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}